import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod/v4";
import { SupportAgentToolName } from "./agents/support-agents/support-agent-tool-names";
import { DateSchema } from "./conversation/shared-schemas";

export enum ConversationAssigneeSummary {
  AI_AGENT_HANDLED = "AI_AGENT_HANDLED",
  MERCHANT_HANDLED = "MERCHANT_HANDLED",

  AI_AGENT_HANDOFF_TO_HUMAN = "AI_AGENT_HANDOFF_TO_HUMAN",

  AI_AGENT_HANDOFF_TO_IGNORED = "AI_AGENT_HANDOFF_TO_IGNORED",
  MERCHANT_IGNORED = "MERCHANT_IGNORED",
}

export const ConversationAssigneeSummarySchema = createEnum(
  ConversationAssigneeSummary,
  { enumName: "ConversationAssigneeSummary" },
);

export enum ConversationCategory {
  SALES = "SALES",
  SUPPORT = "SUPPORT",
  OTHER = "OTHER",
}

export const ConversationCategorySchema = createEnum(ConversationCategory, {
  enumName: "ConversationCategory",
}).describe(
  `SALES - The conversation is to help the customer purchase a product or facilitate a purchase through recommendations or research about the store's product offerings.
SUPPORT - The conversation is to answer a customer's questions about store policies or help them with an issue they're having with the store or a purchase from the store.
OTHER - The conversation is not related to sales or support.`,
);

export enum ConversationResolutionSentiment {
  POSITIVE = "POSITIVE",
  NEUTRAL = "NEUTRAL",
  NEGATIVE = "NEGATIVE",
}

export const ConversationResolutionSentimentSchema = createEnum(
  ConversationResolutionSentiment,
  { enumName: "ConversationResolutionSentiment" },
).describe(
  `POSITIVE - The conversation was resolved in a way that the customer was clearly satisfied with the outcome.
For support tickets, this means that the customer had a message with a positive sentiment after the message where the merchant resolved their issue.
For sales conversations, this just means that the customer did not have a message with a negative sentiment while getting product information, product recommendations, or making a purchase.

NEUTRAL - The conversation was resolved in a way that the customer was neither satisfied nor dissatisfied with the outcome.
For support tickets, this means that the customer either did not respond to the merchant's message where they resolved their issue, or did not express any sentiment explicitly.
For sales conversations, this means that the customer only sent a single message without any explicit sentiment.

NEGATIVE - The conversation was resolved in a way that the customer was clearly dissatisfied with the outcome.
For support tickets, this means that the customer had a message with a negative sentiment after the message where the merchant resolved their issue, or many messages with negative sentiment leading up to the resolution and then no response after the resolution.
For sales conversations, this just means that the customer explicitly expressed negative sentiment while getting product information or product recommendations.`,
);

export enum SupportAction {
  ORDER_STATUS = "ORDER_OR_FULFILLMENT_STATUS",
  RETURN_STATUS = "RETURN_STATUS",
  CANCEL_ORDER = "CANCEL_ORDER",
  EDIT_ORDER = "EDIT_ORDER",
  EDIT_SHIPPING_ADDRESS = "EDIT_SHIPPING_ADDRESS",
  MAKE_RETURN_EXCHANGE_REFUND = "MAKE_RETURN_EXCHANGE_REFUND",
  RESEND_RETURN_LABEL = "RESEND_RETURN_LABEL",
  NOTIFY_FOR_BACK_IN_STOCK = "NOTIFY_FOR_BACK_IN_STOCK",
  SALE_OR_DISCOUNT_ISSUE = "SALE_OR_DISCOUNT_ISSUE",
  POLICY_QUESTION = "POLICY_QUESTION",
  DISCOUNT_REQUEST = "DISCOUNT_REQUEST",
  GIFT_CARD_BALANCE = "GIFT_CARD_BALANCE",
  OTHER_ISSUE = "OTHER_ISSUE",
}

export const SupportActionSchema = createEnum(SupportAction, {
  enumName: "SupportAction",
}).describe(
  `ORDER_STATUS - The customer needs help checking the status of an order's fulfillment or tracking information.
RETURN_STATUS - The customer needs help checking the status of a return, exchange, or refund request.
CANCEL_ORDER - The customer needs help cancelling an order.
EDIT_ORDER - The customer needs help editing an order's quantity or items in the order.
EDIT_SHIPPING_ADDRESS - The customer needs help editing their shipping address for an order.
MAKE_RETURN_EXCHANGE_REFUND - The customer needs help making a return, exchange, or refund.
RESEND_RETURN_LABEL - The customer needs a return label for an existing return request to be sent again.
NOTIFY_FOR_BACK_IN_STOCK - The customer inquired about an out of stock product.
SALE_OR_DISCOUNT_ISSUE - The customer is experiencing an issue with a discount or sale.
POLICY_QUESTION - The customer is asking a question about a store policy.
DISCOUNT_REQUEST - The customer is asking for a lower price for a discount or sale that they were unable to take advantage of.
GIFT_CARD_BALANCE - The customer is checking the balance of a gift card or is trying to use a gift card.
OTHER_ISSUE - The customer is experiencing an issue that does not fit into any of the other categories.`,
);

export const AiGeneratedConversationInsightsSchema = z.object({
  summary: z
    .string()
    .describe(
      "A short summary of the conversation that focuses on what the customer is asking for or what the customer is experiencing and what was done to help them (or why they were not helped, if that reasoning is clear from the support agent's messages), one paragraph or less",
    ),
  highlights: z
    .string()
    .describe(
      "Most important keyword highlights from the conversation, like the customer's core issue/question and the resolution of the conversation, 15 words or less with commas separating each highlight",
    ),
  customerSentiment: ConversationResolutionSentimentSchema,
  customerSentimentReasoning: z
    .string()
    .describe(
      "Short explanation for why you chose the customer sentiment you did, two sentences or less",
    ),
  conversationCategories: z
    .array(
      createEnum(ConversationCategory, { enumName: "ConversationCategory" }),
    )
    .describe("What categories the conversation falls into"),
  supportActions: z
    .array(createEnum(SupportAction, { enumName: "SupportAction" }))
    .describe(
      "Any support actions that the customer requested. Leave empty if the customer did not request any support actions or if the conversation was sales focused.",
    ),
  supportActionsReasoning: z
    .string()
    .describe(
      "Short explanation for why you chose the support actions you did, if any. If the support action is self-explanatory, do not explain it. Always explain OTHER_ISSUE. Limit to one sentence or less per action.",
    ),
});

const ToolCallErrorTypeSchema = createEnum(
  [
    "NETWORK_ERROR",
    "AUTHENTICATION_ERROR",
    "VALIDATION_ERROR",
    "TIMEOUT_ERROR",
    "RATE_LIMIT_ERROR",
    "PERMISSION_ERROR",
    "NOT_FOUND_ERROR",
    "TEAM_NOT_FOUND_ERROR",
    "CONFIGURATION_ERROR",
    "SERVER_ERROR",
    "SHOPIFY_API_ERROR",
    "UNKNOWN_ERROR",
  ],
  { enumName: "ToolCallErrorType" },
);

const ToolCallAnalysisSchema = z.record(
  z.string(),
  z.object({
    total: z.number(),
    successes: z.number(),
    errors: z.object({
      total: z.number(),
      errorTypes: z
        .record(ToolCallErrorTypeSchema, z.number().optional())
        .optional(),
    }),
  }),
);

const SupportAgentToolNameSchema = createEnum(SupportAgentToolName, {
  enumName: "SupportAgentToolName",
});

const SuggestedToolSchema = z.object({
  name: SupportAgentToolNameSchema.describe("The name of the suggested tool"),
  enabled: z
    .boolean()
    .describe("Whether the tool was enabled when the handoff occurred"),
});

const HandoffSchema_ = z.object({
  category: z.string().describe("The category of the AI handoff"),
  reason: z.string().describe("The reason for the AI handoff"),
  suggestedCategory: z
    .string()
    .nullable()
    .describe("AI-suggested category if OTHER was selected, null otherwise"),
  source: z
    .enum(["tool-call", "handoff-detection"])
    .describe("How the handoff was detected"),
  suggestedTool: SuggestedToolSchema.optional().describe(
    "The support agent tool suggested to resolve the customer's request, along with whether it was enabled",
  ),
});

export const HandoffSchema: z.ZodType<HandoffSchema> = HandoffSchema_;
export interface HandoffSchema extends z.infer<typeof HandoffSchema_> {}

const CalculatedConversationInsightsSchema = z.object({
  conversationAssigneeSummary: ConversationAssigneeSummarySchema,
  toolCallAnalysis: ToolCallAnalysisSchema.optional(),
  categories: z.array(z.string()).optional(),
  handoff: HandoffSchema.optional().describe(
    "Information about AI handoff to human, if applicable",
  ),
});

const AllConversationInsightsSchema =
  AiGeneratedConversationInsightsSchema.extend(
    CalculatedConversationInsightsSchema.shape,
  );

export type AllConversationInsights = z.infer<
  typeof AllConversationInsightsSchema
>;

export const ConversationInsightsSchema = z.object({
  insights: AllConversationInsightsSchema.optional(),
  lastInProgressGeneratedAt: DateSchema.optional().describe(
    "If present, the insights were generated for a conversation still in progress at this timestamp. generatedAt should be undefined.",
  ),
  generatedAt: DateSchema.optional().describe(
    "If present, the insights were finalized on a closed conversation at this timestamp",
  ),
});

export type ConversationInsights = z.infer<typeof ConversationInsightsSchema>;

const DateGroupingField_ = z.enum([
  "created_at",
  "closed_at",
  "last_response_at",
]);
export const DateGroupingField: z.ZodType<DateGroupingField> =
  DateGroupingField_;
export type DateGroupingField = z.infer<typeof DateGroupingField_>;

/**
 * Schema for conversation insights item used in tables across admin and merchant apps.
 * This represents the base structure for a conversation with insights data.
 */
const ConversationInsightsItemSchema_ = z.object({
  _id: zExt.objectId(),
  teamId: zExt.objectId(),
  platform: z.string(),
  createdAt: DateSchema,
  closedAt: DateSchema.optional().nullable(),
  lastResponseAt: DateSchema.optional().nullable(),
  conversationInsights: ConversationInsightsSchema.optional(),
});

export const ConversationInsightsItemSchema: z.ZodType<ConversationInsightsItem> =
  ConversationInsightsItemSchema_;

export interface ConversationInsightsItem
  extends z.infer<typeof ConversationInsightsItemSchema_> {}
